import type { FormValidationTranslations } from '@common/i18n/formValidation';
import type { TemplateFormField } from '@common/types/templateFormFields';
import type { TemplateFormLanguage } from '@common/types/templateForms';
import { validate } from '@/utils/forms';
import { isEmptyFormValue, isEmptyRequiredFieldValue } from './types';

export type ValidatableField = Pick<TemplateFormField, 'questionSchema'> &
    Partial<Pick<TemplateFormField, 'questionValidationSchema'>>;

/**
 * Validates a single question's value: required, onChange validators, and date range.
 * Shared by top-level questions and questions nested in contact blocks.
 */
export function validateFieldValue(
    field: ValidatableField,
    value: unknown,
    t: FormValidationTranslations,
    language: TemplateFormLanguage,
): string | null {
    const { questionSchema, questionValidationSchema } = field;

    if (
        questionValidationSchema?.required === true &&
        isEmptyRequiredFieldValue(
            value,
            questionSchema.type,
            questionSchema.columns,
            questionSchema.rowCount,
        )
    ) {
        return t.fieldRequired;
    }

    const onChangeChecks = (questionValidationSchema?.onChange ??
        questionValidationSchema?.onchange) as Record<string, unknown> | undefined;
    if (onChangeChecks) {
        for (const check of Object.keys(onChangeChecks)) {
            const type = check === 'type' ? onChangeChecks[check] : check;
            const extraParam = check === 'type' ? null : onChangeChecks[check];

            if (typeof type !== 'string') continue;

            const validationStatus = validate(
                type,
                typeof value === 'string' || typeof value === 'number' || value instanceof Date
                    ? value
                    : String(value),
                typeof extraParam === 'string' || typeof extraParam === 'number'
                    ? extraParam
                    : null,
                language,
            );

            if (validationStatus !== true) {
                return validationStatus;
            }
        }
    }

    if (questionSchema.type === 'date' && !isEmptyFormValue(value)) {
        const selectedDate = new Date(value as string);
        const selectedYear = selectedDate.getFullYear();
        if (Number.isNaN(selectedDate.getTime()) || selectedYear < 1900 || selectedYear > 2099) {
            return t.invalidDate;
        }
    }

    return null;
}

/** Mirrors the heuristic used to flag email-format errors for the snackbar message. */
export function isEmailField(field: Pick<TemplateFormField, 'title'> & ValidatableField): boolean {
    const title = field.title?.toLowerCase() ?? '';
    return (
        field.questionSchema.name === 'email' ||
        title.includes('email address') ||
        title.includes('adresse e-mail')
    );
}
